import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { HiCheck } from 'react-icons/hi'

interface FeatureBlockProps {
  label: string
  title: string
  description: string
  features: string[]
  imagePlaceholder: string
  reverse?: boolean
}

function FeatureBlock({ label, title, description, features, imagePlaceholder, reverse }: FeatureBlockProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <div ref={ref} className="feature-block" style={reverse ? { direction: 'rtl' } : undefined}>
      <motion.div
        className="feature-visual"
        initial={{ opacity: 0, x: reverse ? 50 : -50 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ direction: 'ltr' }}
      >
        <div className="feature-visual-placeholder">
          {imagePlaceholder}
        </div>
      </motion.div>

      <motion.div
        className="feature-content"
        initial={{ opacity: 0, x: reverse ? -50 : 50 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
        style={{ direction: 'ltr' }}
      >
        <span className="feature-label">{label}</span>
        <h3 className="feature-title">{title}</h3>
        <p className="feature-desc">{description}</p>
        <ul className="feature-list">
          {features.map((feature, index) => (
            <motion.li
              key={index}
              className="feature-list-item"
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
            >
              <HiCheck className="feature-list-icon" />
              <span>{feature}</span>
            </motion.li>
          ))}
        </ul>
      </motion.div>
    </div>
  )
}

export function FeatureShowcase() {
  return (
    <section className="features-section">
      <FeatureBlock
        label="AI-Powered"
        title="Intelligent Bet Builder"
        description="Our Dixon-Coles ML model analyzes thousands of data points to generate intelligent betting suggestions across 60+ markets."
        features={[
          'Per-90 normalized player statistics',
          'Position-aware player prop suggestions',
          'Automatic correlation warnings',
          'Confidence levels based on sample size',
        ]}
        imagePlaceholder="Screenshot: Bet Builder with AngleCards showing market suggestions, confidence levels, and line options"
      />

      <FeatureBlock
        label="Transparent"
        title="No Tips, Just Data"
        description="Every suggestion comes with full transparency. See exactly why a line is recommended with underlying statistics and methodology."
        features={[
          'Sample size and data freshness warnings',
          'Referee strictness impact factors',
          'Derby and relegation battle uplifts',
          'Bayesian shrinkage for small samples',
        ]}
        imagePlaceholder="Screenshot: Probability breakdown tooltip showing raw stats, adjustments, and final probability calculation"
        reverse
      />

      <FeatureBlock
        label="Real-Time"
        title="Live Match Intelligence"
        description="Stats refresh every 1-2 minutes during live matches. Get predicted lineups, head-to-head analysis, and player comparisons in real-time."
        features={[
          'Live possession, shots, corners, cards',
          'Predicted XI with start probability',
          'Historical head-to-head records',
          'Player comparison across all metrics',
        ]}
        imagePlaceholder="Screenshot: Live fixture view with real-time stats, predicted lineup, and player comparison panel"
      />
    </section>
  )
}
